import LocalStorage from 'app-lib/LocalStorage'
import * as mobx from 'mobx'
import { supportedChains } from '../../config/appConfig'
import SettingsStore, { ISettingsStoreProps } from './SettingsStore'

/**
 * Manage the retrieval and persistance of app settings.
 */
export default class SettingsAutoStorage {
  storage: LocalStorage
  settingsStore: SettingsStore
  stopAutoSaveSettings?: mobx.IReactionDisposer

  async getSettings(): Promise<Partial<ISettingsStoreProps>> {
    const settings = await this.storage.get('settings', SettingsStore.defaultSettings)
    supportedChains.forEach((chainId) => {
      // Necessary for when we add new chains; avoids crash
      if (!Array.isArray(settings.favoritePairs[chainId])) {
        settings.favoritePairs[chainId] = []
      }
    })
    return settings
  }

  saveSettings(settings: Partial<ISettingsStoreProps>) {
    return this.storage.save('settings', settings)
  }

  /**
   * Load local settings + setup automatic persistence
   */
  async init() {
    const storedSettings = await this.getSettings()
    this.settingsStore.assign(storedSettings)
    if (this.settingsStore.protocolVersionOverride !== -1) {
      this.settingsStore.setProtocolVersion(this.settingsStore.protocolVersionOverride)
    }

    /**
     * Auto save app settings on every change.
     */
    this.stopAutoSaveSettings = mobx.reaction(
      () => {
        // "We enumerate which props/setting need to be sync so mobx observe them."
        const {
          activePairs,
          debugTx,
          devMode,
          favoritePairs,
          fetchRatesOnChain,
          panelsLayout,
          positionsLayout,
          protocolVersionOverride,
          users,
        } = this.settingsStore
        return {
          activePairs,
          debugTx,
          devMode,
          favoritePairs,
          fetchRatesOnChain,
          panelsLayout,
          positionsLayout,
          protocolVersionOverride,
          users,
        }
      },
      (settings) => this.saveSettings(settings),
      { name: 'autoSaveSettings', delay: 200 }
    )
  }

  destroy() {
    this.stopAutoSaveSettings && this.stopAutoSaveSettings()
  }

  constructor(settingsStore: SettingsStore, storage: LocalStorage) {
    this.settingsStore = settingsStore
    this.storage = storage
  }
}
